import React, { useState } from 'react';
import { useCashClosings } from '../hooks/useCashClosings';
import { useCarts } from '../hooks/useCarts';
import Currency from '../../src/utils/Currency.js';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent
} from './ui/accordion';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from './ui/dropdown-menu';
import { ArrowLeft, ShoppingCart, List, Plus, Save, X, Edit, Trash2, Eye, Check, FileText, Receipt, ExternalLink, Printer as PrintIcon, ChevronDown } from 'lucide-react';

const FALLBACK_DATE = new Date('9999-01-01');

// One collapsible, scrollable box of {title, quantity, total} rows — used for
// the total_card_brand / total_card_type / total_sections breakdowns on the
// read view. `flex-1 basis-[300px]` lets several of these sit in a flex-wrap
// row: as many as fit per row on wide screens, and a lone trailing box grows
// to fill the row instead of staying column-width.
function TotalsBox({ title, rows, renderRowTitle, formatPrice }) {
  return (
    <Accordion type="single" collapsible className="flex-1 basis-[300px] min-w-[260px] bg-card border rounded-md shadow-sm overflow-hidden">
      <AccordionItem value="box" className="mb-0 border-0">
        <AccordionTrigger className="px-3 py-2.5 hover:no-underline">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-black uppercase text-primary">{title}</span>
            <Badge variant="secondary">{rows.length}</Badge>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-3">
          <div className="max-h-[260px] overflow-y-auto flex flex-col gap-1.5">
            {rows.map((row, i) => {
              const rowTitle = renderRowTitle(row);
              return (
                <div key={i} className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {typeof rowTitle === 'string'
                      ? <span className="truncate block">{rowTitle}</span>
                      : rowTitle}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="opacity-60">{row.quantity}</span>
                    <span className="font-bold text-primary">{formatPrice(row.total)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}

export function CashClosingView({ currency, locale, env, client, instanceId, onBack, drawerContainer }) {
  const [currentView, setCurrentView] = useState('cash_closings'); // 'cash_closings', 'carts', 'create_form', 'edit_form', 'read_view'
  const [editingCashClosingId, setEditingCashClosingId] = useState(null);
  const [previewDocUrl, setPreviewDocUrl] = useState(null);
  const [previewDocTitle, setPreviewDocTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default range: Beginning of the current labor day (6 AM)
  const getDefaultStartDate = () => {
    const d = new Date();
    if (d.getHours() < 6) {
      d.setDate(d.getDate() - 1);
    }
    d.setHours(6, 0, 0, 0);

    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const getDefaultEndDate = () => {
    const d = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // State for the Carts view tab
  const [startDateStr, setStartDateStr] = useState(getDefaultStartDate);
  const [endDateStr, setEndDateStr] = useState(getDefaultEndDate);

  // State for the Form view tab
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStartDateStr, setFormStartDateStr] = useState(getDefaultStartDate);
  const [formEndDateStr, setFormEndDateStr] = useState(getDefaultEndDate);

  // Refs to hold the last valid dates so quick typing doesn't crash the Firestore query
  const lastValidStartDate = React.useRef(new Date(startDateStr));
  const startDate = React.useMemo(() => {
    const d = new Date(startDateStr);
    if (!isNaN(d)) {
      d.setSeconds(0, 0);
      lastValidStartDate.current = d;
    }
    return lastValidStartDate.current;
  }, [startDateStr]);

  const lastValidEndDate = React.useRef(new Date(endDateStr));
  const endDate = React.useMemo(() => {
    const d = new Date(endDateStr);
    if (!isNaN(d)) {
      d.setSeconds(59, 999);
      lastValidEndDate.current = d;
    }
    return lastValidEndDate.current;
  }, [endDateStr]);

  const lastValidFormStartDate = React.useRef(new Date(formStartDateStr));
  const formStartDate = React.useMemo(() => {
    const d = new Date(formStartDateStr);
    if (!isNaN(d)) {
      d.setSeconds(0, 0);
      lastValidFormStartDate.current = d;
    }
    return lastValidFormStartDate.current;
  }, [formStartDateStr]);

  const lastValidFormEndDate = React.useRef(new Date(formEndDateStr));
  const formEndDate = React.useMemo(() => {
    const d = new Date(formEndDateStr);
    if (!isNaN(d)) {
      d.setSeconds(59, 999);
      lastValidFormEndDate.current = d;
    }
    return lastValidFormEndDate.current;
  }, [formEndDateStr]);

  const generateSuggestedName = (startDateObj, endDateObj) => {
    if (!startDateObj || isNaN(startDateObj) || !endDateObj || isNaN(endDateObj)) return '';

    const pad = (n) => n.toString().padStart(2, '0');

    const formatDatePart = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const formatTimePart = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    const formatTimezone = (d) => {
      const tzo = -d.getTimezoneOffset();
      const dif = tzo >= 0 ? '+' : '-';
      const offH = pad(Math.floor(Math.abs(tzo) / 60));
      const offM = pad(Math.abs(tzo) % 60);
      return `GMT${dif}${offH}:${offM}`;
    };

    const startDay = formatDatePart(startDateObj);
    const endDay = formatDatePart(endDateObj);
    const timezone = formatTimezone(startDateObj);

    if (startDay === endDay) {
      return `${startDay} ${formatTimePart(startDateObj)} a ${formatTimePart(endDateObj)} ${timezone}`;
    } else {
      return `${startDay} ${formatTimePart(startDateObj)} a ${endDay} ${formatTimePart(endDateObj)} ${timezone}`;
    }
  };

  const suggestedName = React.useMemo(() => generateSuggestedName(formStartDate, formEndDate), [formStartDate, formEndDate]);

  // Which dates to pass to useCarts depends on the current active view
  const isFormView = currentView === 'create_form' || currentView === 'edit_form' || currentView === 'read_view';
  const activeStartDate = isFormView ? formStartDate : startDate;
  const activeEndDate = isFormView ? formEndDate : endDate;

  const validateDateRange = (start, end) => {
    if (!start || !end || isNaN(start) || isNaN(end)) return null;
    if (start > end) return "La fecha de inicio no puede ser mayor a la de término.";
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 35) return "El rango de fechas no puede exceder los 35 días.";
    return null;
  };

  const activeDateError = React.useMemo(() => validateDateRange(activeStartDate, activeEndDate), [activeStartDate, activeEndDate]);

  // Si hay error en las fechas, pasamos fechas imposibles para evitar consultas masivas a Firestore
  const safeStartDate = activeDateError ? FALLBACK_DATE : activeStartDate;
  const safeEndDate = activeDateError ? FALLBACK_DATE : activeEndDate;

  const { cashClosings, loadedCashClosings } = useCashClosings(env, client, instanceId);
  const { carts, loadedCarts } = useCarts(env, client, safeStartDate, safeEndDate, instanceId);

  // API Call helper
  const callManageAPI = async (method, body = null, id = null, extraQuery = '') => {
    let url = `https://managecashclosings-x3herayjcq-uc.a.run.app?environment=${env}&client=${client}`;
    if (id) url += `&id=${id}`;
    if (extraQuery) url += `&${extraQuery}`;

    const config = {
      method,
      headers: { 'Content-Type': 'application/json' }
    };
    if (body) config.body = JSON.stringify(body);

    const response = await fetch(url, config);
    if (!response.ok) {
      let errText = response.statusText;
      try {
        const errJson = await response.json();
        errText = errJson.message || errText;
      } catch (e) { }
      throw new Error(`API failed: ${errText}`);
    }

    // Handles empty responses safely
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  };

  // Extract / Formatting helpers
  const formatDate = (val) => {
    if (!val) return 'N/A';
    if (val.toDate && typeof val.toDate === 'function') {
      return val.toDate().toLocaleString();
    }
    try {
      return new Date(val).toLocaleString();
    } catch {
      return val.toString();
    }
  };

  const formatPrice = (price) => {
    if (price == null) return new Currency(currency, locale).format(0);
    return new Currency(currency, locale).format(price);
  };

  const handleOpenCreate = () => {
    const startStr = getDefaultStartDate();
    const endStr = getDefaultEndDate();

    setFormName('');
    setFormDescription('');
    setFormStartDateStr(startStr);
    setFormEndDateStr(endStr);
    setEditingCashClosingId(null);
    setCurrentView('create_form');
  };

  const populateFormWithCC = (cc) => {
    setFormName(cc.name || '');
    setFormDescription(cc.description || '');

    const formatForInput = (val) => {
      if (!val) return getDefaultStartDate();
      let d;
      if (val.toDate && typeof val.toDate === 'function') d = val.toDate();
      else d = new Date(val);

      if (isNaN(d)) return getDefaultStartDate();
      const pad = (n) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    setFormStartDateStr(formatForInput(cc.start_date));
    setFormEndDateStr(formatForInput(cc.end_date));
    setEditingCashClosingId(cc.id);
  };

  const handleEdit = (e, cc) => {
    e.stopPropagation();
    populateFormWithCC(cc);
    setCurrentView('edit_form');
  };

  const handleRead = (e, cc) => {
    e.stopPropagation();
    populateFormWithCC(cc);
    setCurrentView('read_view');
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("¿Estás seguro de eliminar este cierre de caja?")) return;
    setIsSubmitting(true);
    try {
      await callManageAPI('DELETE', null, id);
    } catch (error) {
      console.error(error);
      alert("Error eliminando el cierre de caja: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (currentView === 'read_view') return;

    setIsSubmitting(true);
    try {
      const payload = {
        name: formName.trim() || suggestedName,
        description: formDescription,
        start_date: formStartDate.toISOString(),
        end_date: formEndDate.toISOString(),
        instance_id: instanceId
      };

      let result;
      if (currentView === 'edit_form') {
        result = await callManageAPI('PUT', payload, editingCashClosingId);
      } else {
        payload.print = true;
        result = await callManageAPI('POST', payload);
      }

      // If the API returns the created/updated object, use it. Otherwise find it in the list.
      const ccId = result?.id || editingCashClosingId || (result?.[0]?.id);

      if (ccId) {
        setEditingCashClosingId(ccId);
        setCurrentView('read_view');
      } else {
        setCurrentView('cash_closings');
        setEditingCashClosingId(null);
      }
    } catch (error) {
      console.error(error);
      alert("Error guardando el cierre de caja: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = async (ccId, action = 'print') => {
    setIsSubmitting(true);
    try {
      const extraQuery = action === 'generate' ? 'generate=true' : 'print=true';
      await callManageAPI('GET', null, ccId, extraQuery);
    } catch (error) {
      console.error(error);
      alert("Error en la solicitud: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const previewCarts = React.useMemo(() => {
    if (!carts) return null;
    if (currentView === 'edit_form' || currentView === 'read_view') {
      return carts.filter(cart => cart.cash_closing === editingCashClosingId);
    }
    return carts.filter(cart => cart.processed_sale === true && !cart.cash_closing);
  }, [carts, currentView, editingCashClosingId]);

  const previewCartTotal = React.useMemo(() => {
    if (!previewCarts) return 0;
    const validCarts = previewCarts.filter(cart => cart.processed_sale === true);
    return validCarts.reduce((acc, cart) => acc + (Number(cart.common_total) || 0), 0);
  }, [previewCarts]);

  const cartsListTotal = React.useMemo(() => {
    if (!carts) return 0;
    const paidCarts = carts.filter(cart => cart.processed_sale === true);
    return paidCarts.reduce((acc, cart) => acc + (Number(cart.common_total) || 0), 0);
  }, [carts]);

  const renderCartAccordion = (cartsData) => (
    <Accordion type="multiple" className="w-full bg-card rounded-md shadow p-4 overflow-y-auto max-h-[70vh]">
      {cartsData?.map((cart) => {
        const isClosed = !!cart.cash_closing;
        const isPaid = cart.processed_sale === true;

        return (
          <AccordionItem key={cart.id} value={cart.id} className="shadow-md rounded">
            <AccordionTrigger className="hover:no-underline hover:bg-emerald-200/50 px-2 rounded data-[state=open]:bg-emerald-200/50 transition-colors">
              <div className="flex justify-between w-full items-center mr-4">
                {/* Left Section: Status & Details */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    {isClosed ? (
                      <div className="bg-emerald-800 w-[60px] text-white px-2.5 py-1 rounded-[10px] flex flex-col items-center gap-2 shadow-sm">
                        <Check size={14} title={cart.cash_closing} className="cursor-help" />
                        <span className="text-[8px] font-bold uppercase text-center">En cierre de caja</span>
                      </div>
                    ) : isPaid ? (
                      <div className="w-[60px] flex flex-col items-center gap-2 text-emerald-600">
                        <Check size={18} />
                        <span className="text-[8px] font-bold uppercase text-center">Pagado</span>
                      </div>
                    ) : (
                      <div className="w-[60px] flex flex-col items-center gap-2 text-slate-400">
                        <X size={18} />
                        <span className="text-[8px] font-bold uppercase text-center">No Pagado</span>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col items-start gap-0.5 text-left border-l pl-4 border-slate-200">
                    {cart.sale_code && <span className="text-xs font-black">Pedido {cart.sale_code}</span>}
                    <span className="font-semibold text-[10px] opacity-60">ID: {cart.id}</span>
                    <span className="text-xs font-bold">{formatDate(cart.created_at)}</span>
                  </div>
                </div>

                {/* Right Section: Price */}
                <div className="flex items-center gap-3">
                  <span className={isClosed || isPaid ? "font-black text-lg text-primary" : "font-black text-lg text-slate-400"}>{formatPrice(cart.common_total)}</span>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pt-4 pb-6 text-[11px] mt-1 bg-emerald-200/50 shadow-md border-muted-foreground/20 rounded-md">
              <div className="flex flex-col gap-5">
                {/* 1. Header Metadata Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="flex flex-col gap-1 p-2 bg-card rounded border shadow-sm">
                    <span className="opacity-50 font-bold uppercase text-[8px]">ID de Pago</span>
                    <span className="font-bold">{cart.payment_id || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-1 p-2 bg-card rounded border shadow-sm">
                    <span className="opacity-50 font-bold uppercase text-[8px]">Orden Externa</span>
                    <span className="font-bold">{cart.external_order_id || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-1 p-2 bg-card rounded border shadow-sm">
                    <span className="opacity-50 font-bold uppercase text-[8px]">Tienda</span>
                    <span className="font-bold truncate" title={cart.store_id}>{cart.store_id || 'N/A'}</span>
                  </div>
                  <div className="flex flex-col gap-1 p-2 bg-card rounded border shadow-sm">
                    <span className="opacity-50 font-bold uppercase text-[8px]">Instancia</span>
                    <span className="font-mono text-[9px] truncate" title={cart.instance_id}>{cart.instance_id || 'N/A'}</span>
                  </div>
                </div>

                {/* 2. Documents Section */}
                {(cart.client_ticket_url || cart.client_document_url) && (
                  <div className="flex gap-3 items-center border-y py-3 border-muted-foreground/10">
                    <span className="font-bold uppercase text-[9px] opacity-60 mr-2">Documentos:</span>
                    {cart.client_ticket_url && (
                      <button
                        onClick={() => {
                          setPreviewDocUrl(cart.client_ticket_url);
                          setPreviewDocTitle("Vista Previa: Ticket de Venta");
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 rounded hover:bg-emerald-500/20 transition-all font-bold cursor-pointer"
                      >
                        <Receipt size={14} /> Ticket
                      </button>
                    )}
                    {cart.client_document_url && (
                      <button
                        onClick={() => {
                          setPreviewDocUrl(cart.client_document_url);
                          setPreviewDocTitle("Vista Previa: Documento Tributario");
                        }}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 text-blue-600 border border-blue-500/20 rounded hover:bg-blue-500/20 transition-all font-bold cursor-pointer"
                      >
                        <FileText size={14} /> Documento
                      </button>
                    )}
                  </div>
                )}

                {/* 3. Items Detail Section */}
                <div className="flex flex-col gap-2">
                  <h4 className="font-black uppercase text-[9px] opacity-40 mb-1">Detalle de Productos</h4>
                  <div className="space-y-3">
                    {cart.details?.map((item, idx) => (
                      <div key={`${item.id}-${idx}`} className="flex flex-col gap-1.5 border-b border-muted-foreground/5 pb-2 last:border-0">
                        <div className="flex justify-between items-start">
                          <div className="flex gap-2 items-center">
                            <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-black text-[10px]">{item.quantity}x</span>
                            <span className="font-bold text-[12px]">{item.label || item.name}</span>
                          </div>
                          <span className="font-bold text-primary">{formatPrice(item.common_total)}</span>
                        </div>

                        {/* Nested Modifiers */}
                        {item.selected_modifiers?.length > 0 && (
                          <div className="flex flex-col gap-1 ml-6 border-l-2 border-emerald-500/20 pl-3">
                            {item.selected_modifiers.map((mod, mIdx) => (
                              <div key={mIdx} className="flex justify-between items-center opacity-70 italic text-[10px]">
                                <span>↳ {mod.quantity && mod.quantity > 1 ? `${mod.quantity}x ` : ''}{mod.name}</span>
                                {mod.extra_amount > 0 && (
                                  <span className="text-[9px] font-medium">+{formatPrice(mod.extra_amount * (mod.quantity || 1))}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Notes if any */}
                        {item.notes && (
                          <p className="ml-6 text-[10px] opacity-50 italic">Nota: {item.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* 4. Associate ID if present */}
                {cart.cash_closing && (
                  <div className="mt-2 pt-2 border-t border-muted-foreground/10 text-[9px] opacity-40">
                    Cierre asociado: <span className="font-mono">{cart.cash_closing}</span>
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </Accordion>
  );

  const renderCashClosings = () => (
    <div className="w-full flex flex-col gap-4">
      <h2 className="text-2xl font-bold">Listado de cierres de caja</h2>
      {!loadedCashClosings ? (
        <p className="">Cargando cierres de caja...</p>
      ) : cashClosings?.length === 0 ? (
        <p className="">No se encontraron cierres de caja.</p>
      ) : (
        <Accordion type="multiple" className="w-full bg-card p-4 overflow-y-auto max-h-[80vh] shadow-md">
          {cashClosings?.map((cc) => (
            <AccordionItem key={cc.id} value={cc.id} className="shadow-md rounded-md">
              <AccordionTrigger className="hover:no-underline hover:bg-emerald-200/50 px-2 rounded data-[state=open]:bg-emerald-200/50 transition-colors">
                <div className="flex flex-1 justify-between items-center mr-4 gap-4">
                  <div className="z-shape bg-emerald-800 w-[60px] text-white px-2.5 py-1 flex flex-col justify-between items-end gap-1">
                    <span className="text-[12px] font-bold text-center"><b className='text-[9px]'></b> {cc.index}</span>
                    <span className="text-[12px] font-bold text-center"><b className='text-[6px]'>inst.</b> {cc.instance_index}</span>
                  </div>
                  <span className="font-semibold text-center">{cc.name || `Cash Closing #${cc.index}`}</span>
                  <span className="font-bold text-right w-[80px]">{formatPrice(cc.common_total)}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pt-4 pb-6 text-sm mt-1 bg-emerald-200/50 rounded-md shadow-inner">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Financial Section */}
                  <div className="flex flex-col gap-3 p-3 bg-card rounded-md border shadow-sm">
                    <h4 className="text-[10px] font-black uppercase text-primary border-b pb-1 mb-1">Resumen Financiero</h4>
                    <div className="flex justify-between items-center"><span className="opacity-60">Total Bruto:</span> <span className="font-bold">{formatPrice(cc.common_total)}</span></div>
                    <div className="flex justify-between items-center"><span className="opacity-60">Neto:</span> <span className="font-medium">{formatPrice(cc.net_amount)}</span></div>
                    <div className="flex justify-between items-center"><span className="opacity-60">Iva:</span> <span className="font-medium">{formatPrice(cc.tax_amount)}</span></div>
                    <div className="flex justify-between items-center"><span className="opacity-60">Descuentos:</span> <span className="font-medium text-destructive">{formatPrice(cc.common_discounts)}</span></div>
                  </div>

                  {/* Metadata Section */}
                  <div className="flex flex-col gap-3 p-3 bg-card rounded-md border shadow-sm">
                    <h4 className="text-[10px] font-black uppercase text-primary border-b pb-1 mb-1">Detalles de Operación</h4>
                    <div className="flex justify-between items-center"><span className="opacity-60">Carts:</span> <Badge variant="secondary" className="font-bold">{cc.cart_ids?.length || 0}</Badge></div>
                    <div className="flex justify-between items-center"><span className="opacity-60">Instancia:</span> <span className="font-mono text-[10px] text-white border border-muted bg-muted/80 px-1.5 py-0.5 rounded">{cc.instance_id}</span></div>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      <div className="bg-emerald-500/30 border border-emerald-500/50 rounded p-1.5 text-center">
                        <p className="text-[8px] uppercase font-bold opacity-70">Z Global</p>
                        <p className="font-black">{cc.index}</p>
                      </div>
                      <div className="bg-blue-500/30 border border-blue-500/50 rounded p-1.5 text-center">
                        <p className="text-[8px] uppercase font-bold opacity-70">Z Instancia</p>
                        <p className="font-black">{cc.instance_index}</p>
                      </div>
                    </div>
                  </div>

                  {/* Timestamps Section */}
                  <div className="flex flex-col gap-3 p-3 bg-card rounded-md border shadow-sm lg:col-span-1 md:col-span-2 lg:col-span-1">
                    <h4 className="text-[10px] font-black uppercase text-primary border-b pb-1 mb-1">Rango y Registro</h4>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold opacity-60 uppercase">Desde:</span>
                      <span className="font-medium text-xs break-all">{formatDate(cc.start_date)}</span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-[10px] font-bold opacity-60 uppercase">Hasta:</span>
                      <span className="font-medium text-xs break-all">{formatDate(cc.end_date)}</span>
                    </div>
                    <div className="mt-2 pt-2 border-t flex flex-col gap-1 opacity-60 text-[10px]">
                      <div className="flex justify-between italic"><span>Creado:</span> <span>{formatDate(cc.created_at)}</span></div>
                      <div className="flex justify-between italic"><span>Actualizado:</span> <span>{formatDate(cc.updated_at)}</span></div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-between items-center border-t pt-4 border-muted-foreground/20">
                  <div className="text-[10px] opacity-40 font-mono">ID: {cc.id}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => handleRead(e, cc)}
                      disabled={isSubmitting}
                      className="flex items-center gap-1.5 px-3 py-1.5 btn btn-sm btn-primary text-xs font-semibold rounded disabled:opacity-50 transition-all !font-sans"
                    >
                      <Eye size={14} /> Ver Reporte
                    </button>
                    <button
                      onClick={(e) => handleEdit(e, cc)}
                      disabled={isSubmitting}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary text-secondary-foreground text-xs font-semibold rounded hover:brightness-110 disabled:opacity-50 transition-all font-sans"
                    >
                      <Edit size={14} /> Editar
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, cc.id)}
                      disabled={isSubmitting}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-destructive text-destructive-foreground text-xs font-semibold rounded hover:brightness-110 disabled:opacity-50 transition-all font-sans"
                    >
                      <Trash2 size={14} /> Eliminar
                    </button>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );

  const renderCartsList = () => (
    <div className="w-full flex flex-col gap-4">
      <div className="flex flex-col justify-between gap-4">
        <h2 className="text-2xl font-bold">Carritos</h2>
        <div className="flex gap-2 items-center justify-center bg-back rounded-[20px] p-[30px] shadow-[0_2px_12px_rgba(0,0,0,0.1)]">
          <div className="flex flex-col">
            <label className="text-[10px] uppercase font-bold ">Fecha Inicio</label>
            <input
              type="datetime-local"
              className="bg-transparent border-b outline-none focus:border-primary "
              value={startDateStr}
              onChange={(e) => setStartDateStr(e.target.value)}
            />
          </div>
          <span className=" font-semibold px-1">-</span>
          <div className="flex flex-col">
            <label className="text-[10px] uppercase font-bold ">Fecha Fin</label>
            <input
              type="datetime-local"
              className="bg-transparent border-b outline-none focus:border-primary "
              value={endDateStr}
              onChange={(e) => setEndDateStr(e.target.value)}
            />
          </div>
        </div>
      </div>

      {!loadedCarts ? (
        <p className="">Cargando Carritos...</p>
      ) : (!isFormView && activeDateError) ? (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-md shadow-sm font-semibold">
          {activeDateError}
        </div>
      ) : carts?.length === 0 ? (
        <p className="">No se encontraron carritos en el rango de fechas seleccionado.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="bg-card rounded-md shadow-md p-4 flex justify-between items-center border border-muted-foreground/20">
            <div className="flex flex-col">
              <span className="text-sm font-bold uppercase opacity-80 text-primary">Total Recaudado</span>
              <span className="text-[10px] opacity-60">Vista previa de sumas en este rango</span>
            </div>
            <span className="text-2xl font-black text-primary">{formatPrice(cartsListTotal)}</span>
          </div>
          {renderCartAccordion(carts)}
        </div>
      )}
    </div>
  );

  const renderCreateForm = () => (
    <div className="w-full flex flex-col gap-8">
      {/* Left Column: Form */}
      <div className="w-full flex flex-col gap-4">
        <h2 className="text-2xl font-bold">
          {currentView === 'edit_form' && 'Editar Cierre de Caja'}
          {currentView === 'read_view' && 'Detalles Cierre de Caja'}
          {currentView === 'create_form' && 'Nuevo Cierre de Caja'}
        </h2>
        <form onSubmit={handleCreateSubmit} className="bg-card border rounded-md shadow p-6 flex flex-col gap-4">

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold ">Nombre</label>
            {currentView === 'read_view' ? (
              <div className="border p-2 rounded bg-muted/30  font-medium">
                {formName || <span className="opacity-50 italic">Sin nombre</span>}
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                <input
                  type="text"
                  placeholder={suggestedName}
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="border p-2 rounded outline-none focus:border-primary bg-background "
                  disabled={isSubmitting}
                />
                {!formName.trim() && (
                  <p className="text-xs text-muted-foreground ml-1">
                    Sugerencia: <Badge variant="secondary" className="font-normal cursor-pointer hover:bg-muted" onClick={() => setFormName(suggestedName)}>{suggestedName}</Badge>
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-semibold ">Descripción</label>
            {currentView === 'read_view' ? (
              <div className="border p-2 rounded bg-muted/30  whitespace-pre-wrap min-h-[80px]">
                {formDescription || <span className="opacity-50 italic">Sin descripción</span>}
              </div>
            ) : (
              <textarea
                rows={3}
                placeholder="Notas opcionales..."
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className="border p-2 rounded outline-none focus:border-primary bg-background  resize-none"
                disabled={isSubmitting}
              />
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold ">Fecha Inicio</label>
              {(currentView === 'edit_form' || currentView === 'read_view') ? (
                <div className="border p-2 rounded bg-muted/50 text-muted-foreground">
                  {formatDate(formStartDateStr)}
                </div>
              ) : (
                <input
                  type="datetime-local"
                  value={formStartDateStr}
                  onChange={(e) => setFormStartDateStr(e.target.value)}
                  className="border p-2 rounded outline-none focus:border-primary bg-background "
                  required
                  disabled={isSubmitting}
                />
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-semibold ">Fecha Fin</label>
              {(currentView === 'edit_form' || currentView === 'read_view') ? (
                <div className="border p-2 rounded bg-muted/50 text-muted-foreground">
                  {formatDate(formEndDateStr)}
                </div>
              ) : (
                <input
                  type="datetime-local"
                  value={formEndDateStr}
                  onChange={(e) => setFormEndDateStr(e.target.value)}
                  className="border p-2 rounded outline-none focus:border-primary bg-background "
                  required
                  disabled={isSubmitting}
                />
              )}
            </div>
          </div>

          {currentView === 'read_view' && (() => {
            const cc = cashClosings?.find(c => c.id === editingCashClosingId);
            if (!cc) return null;
            return (
              <div className="mt-4 pt-4 border-t border-slate-200 flex flex-col gap-4 animate-in fade-in slide-in-from-top-1">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1 p-2 bg-emerald-100 rounded border border-emerald-100">
                    <span className="text-[10px] font-black uppercase text-emerald-700">Total Recaudado</span>
                    <span className="text-xl font-black text-emerald-800">{formatPrice(cc.common_total)}</span>
                  </div>
                  <div className="flex flex-col gap-1 p-2 bg-blue-100 rounded border border-blue-100">
                    <span className="text-[10px] font-black uppercase text-blue-700">Total Carritos</span>
                    <span className="text-xl font-black text-blue-800">{cc.cart_ids?.length || 0}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div className="flex flex-col p-2 rounded border text-center">
                    <span className="text-[8px] font-bold uppercase opacity-60">Neto</span>
                    <span className="text-xs font-bold">{formatPrice(cc.net_amount)}</span>
                  </div>
                  <div className="flex flex-col p-2 rounded border text-center">
                    <span className="text-[8px] font-bold uppercase opacity-60">IVA</span>
                    <span className="text-xs font-bold">{formatPrice(cc.tax_amount)}</span>
                  </div>
                  <div className="flex flex-col p-2 rounded border text-center">
                    <span className="text-[8px] font-bold uppercase opacity-60">Desc.</span>
                    <span className="text-xs font-bold text-destructive">{formatPrice(cc.common_discounts)}</span>
                  </div>
                </div>

                <div className="bg-slate-100/50 p-3 rounded text-[10px] flex flex-col gap-2">
                  <div className="flex justify-between"><span>Instancia ID:</span> <span className="font-mono">{cc.instance_id}</span></div>
                  <div className="flex justify-between"><span>Z Indice:</span> <span className="font-bold">{cc.index}</span></div>
                  <div className="flex justify-between"><span>Z Instancia:</span> <span className="font-bold">{cc.instance_index}</span></div>
                  <div className="pt-2 border-t border-slate-200 flex justify-between opacity-60 italic">
                    <span>Registro: {formatDate(cc.created_at)}</span>
                  </div>
                </div>

                {(cc.total_card_brand?.length > 0 || cc.total_card_type?.length > 0 || cc.total_sections?.length > 0) && (
                  <div className="flex flex-wrap gap-4">
                    {cc.total_card_brand?.length > 0 && (
                      <TotalsBox
                        title="Por marca de tarjeta"
                        rows={cc.total_card_brand}
                        formatPrice={formatPrice}
                        renderRowTitle={(r) => r.card_brand_label || r.card_type_label || r.card_brand}
                      />
                    )}
                    {cc.total_card_type?.length > 0 && (
                      <TotalsBox
                        title="Por tipo de tarjeta"
                        rows={cc.total_card_type}
                        formatPrice={formatPrice}
                        renderRowTitle={(r) => r.card_brand_label || r.card_type_label || r.card_brand}
                      />
                    )}
                    {cc.total_sections?.length > 0 && (
                      <TotalsBox
                        title="Por secciones"
                        rows={cc.total_sections}
                        formatPrice={formatPrice}
                        renderRowTitle={(r) => (
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Badge variant="outline" className="shrink-0">{r.cash_closing_section_name}</Badge>
                            <span className="truncate">{r.title}</span>
                          </div>
                        )}
                      />
                    )}
                  </div>
                )}

                {/* Actions in Read View */}
                <div className="mt-2 flex gap-2 w-full">
                  {cc.document_url && (
                    <button
                      type="button"
                      onClick={() => {
                        setPreviewDocUrl(cc.document_url);
                        setPreviewDocTitle("Vista Previa: Documento de Cierre");
                      }}
                      disabled={isSubmitting}
                      className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3 bg-blue-100 text-blue-700 border border-blue-200 font-bold rounded-lg shadow-sm hover:bg-blue-200 disabled:opacity-50 transition-all text-xs sm:text-sm text-center leading-tight"
                    >
                      <Eye size={18} className="shrink-0" /> <span>Ver Documento</span>
                    </button>
                  )}

                  <div className={`flex shadow-md rounded-lg ${cc.document_url ? 'flex-1' : 'w-full'}`}>
                    <button
                      type="button"
                      onClick={() => handlePrint(cc.id, 'print')}
                      disabled={isSubmitting}
                      className={`flex flex-1 items-center justify-center gap-1.5 sm:gap-2 py-3 bg-emerald-600 text-white font-black hover:bg-emerald-700 disabled:opacity-50 transition-all uppercase text-[10px] sm:text-xs text-center leading-tight ${cc.document_url ? 'rounded-l-lg' : 'rounded-lg w-full'}`}
                    >
                      <PrintIcon size={18} className="shrink-0" /> <span>{isSubmitting ? 'Procesando...' : 'Imprimir Reporte de Cierre'}</span>
                    </button>
                    {cc.document_url && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            disabled={isSubmitting}
                            className="flex items-center justify-center px-2 sm:px-3 bg-emerald-700 text-white hover:bg-emerald-800 rounded-r-lg disabled:opacity-50 border-l border-emerald-800/50 transition-all shrink-0"
                          >
                            <ChevronDown size={18} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-emerald-700 hover:bg-emerald-800 border-0">
                          <DropdownMenuItem onClick={() => handlePrint(cc.id, 'generate')} disabled={isSubmitting} className="cursor-pointer gap-2 font-medium">
                            <FileText size={16} /> Volver a generar documento
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {currentView !== 'read_view' && isFormView && activeDateError && (
            <div className="text-xs font-bold text-destructive px-1">{activeDateError}</div>
          )}
          {currentView !== 'read_view' && (
            <button
              type="submit"
              disabled={isSubmitting || !!(isFormView && activeDateError)}
              className="mt-4 flex items-center justify-center gap-2 w-full py-2 bg-primary text-primary-foreground font-bold rounded shadow-sm hover:brightness-110 disabled:opacity-50 transition-all"
            >
              <Save size={18} /> {isSubmitting ? 'Guardando...' : (currentView === 'edit_form' ? 'Actualizar Cierre' : 'Crear Cierre')}
            </button>
          )}
        </form>
      </div>

      {/* Right Column: Preview of Carts */}
      <div className="w-full flex flex-col gap-4">
        <div className="flex justify-between items-end border-b pb-2">
          <h2 className="text-xl font-bold flex flex-col gap-1">
            <span>Carts Preview</span>
            <span className="text-sm font-normal ">Carritos en el rango de fechas seleccionado</span>
          </h2>
        </div>

        {!loadedCarts ? (
          <p className="">Cargando vista previa...</p>
        ) : (isFormView && activeDateError) ? (
          <div className="bg-destructive/10 border border-destructive/20 text-destructive p-4 rounded-md shadow-sm font-semibold">
            {activeDateError}
          </div>
        ) : previewCarts?.length === 0 ? (
          <p className="">No hay carritos pagados elegibles para cerrar en este rango de fechas.</p>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="bg-card rounded-md shadow-md p-4 flex justify-between items-center border border-muted-foreground/20">
              <div className="flex flex-col">
                <span className="text-sm font-bold uppercase opacity-80 text-primary">Total a Cerrar</span>
                <span className="text-[10px] opacity-60">Suma de carritos pagados a incluir</span>
              </div>
              <span className="text-2xl font-black text-primary">{formatPrice(previewCartTotal)}</span>
            </div>
            {renderCartAccordion(previewCarts)}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-screen bg-back  p-8 gap-6 max-w-7xl mx-auto w-full">

      {/* HEADER */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex justify-between gap-2 w-full">
          {onBack && (
            <button
              onClick={() => {
                if (window.confirm("¿Estás seguro de que deseas volver al Kiosco?")) {
                  onBack();
                }
              }}
              disabled={isSubmitting}
              className="btn btn-inverted btn-sm flex items-center gap-2"
            >
              <ArrowLeft size={16} /> Volver al kiosko
            </button>
          )}

          {isFormView ? (
            <button
              onClick={() => setCurrentView('cash_closings')}
              disabled={isSubmitting}
              className="btn btn-inverted btn-sm flex items-center gap-2"
            >
              <X size={16} /> {currentView === 'read_view' ? 'Salir' : (currentView === 'edit_form' ? 'Cancelar Edición' : 'Cancelar Creación')}
            </button>
          ) : (
            <button
              onClick={handleOpenCreate}
              disabled={isSubmitting}
              className="btn btn-primary btn-sm flex items-center gap-2 !font-sans"
            >
              <Plus size={16} /> Crear Cierre de Cajas
            </button>
          )}

          {!isFormView && (
            <>
              {currentView === 'cash_closings' ? (
                <button
                  onClick={() => setCurrentView('carts')}
                  disabled={isSubmitting}
                  className="btn btn-inverted btn-sm flex items-center gap-2"
                >
                  <ShoppingCart size={16} /> Ver Lista de Carritos
                </button>
              ) : (
                <button
                  onClick={() => setCurrentView('cash_closings')}
                  disabled={isSubmitting}
                  className="btn btn-inverted btn-sm flex items-center gap-2"
                >
                  <List size={16} /> Ver Cierres de Caja
                </button>
              )}
            </>
          )}
        </div>

      </div>

      <div className="flex gap-2 items-center flex-wrap justify-center">
        <Badge variant="outline" className="bg-card shadow-sm text-xs py-1 px-3"><b>Entorno:</b> {env}</Badge>
        <Badge variant="outline" className="bg-card shadow-sm text-xs py-1 px-3"><b>Cliente:</b> {client}</Badge>
        <Badge variant="outline" className="bg-card shadow-sm text-xs py-1 px-3"><b>Instancia:</b> {instanceId}</Badge>
      </div>

      {/* VIEW CONTENT */}
      <div className="flex flex-1">
        {currentView === 'cash_closings' && renderCashClosings()}
        {currentView === 'carts' && renderCartsList()}
        {isFormView && renderCreateForm()}
      </div>

      {previewDocUrl && (
        <Dialog open={!!previewDocUrl} onOpenChange={(open) => !open && setPreviewDocUrl(null)}>
          <DialogContent className="max-w-[90vw] md:max-w-[800px] h-[90vh] flex flex-col p-1 gap-0 overflow-hidden bg-back sm:max-w-lg rounded-lg" container={drawerContainer}>
            <DialogHeader className="p-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye size={18} className="text-primary" />
                {previewDocTitle}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 w-full bg-muted">
              <iframe
                src={previewDocUrl}
                className="w-full h-full border-none"
                title="Document Preview"
              />
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}